/**
 * Builder selection → POST /api/report-builder/network-report body (the
 * combined form: one `networks[]` entry per network, report-level fields on
 * top). Built on Generate / Regenerate so the payload can be checked before
 * the endpoint is wired in.
 *
 * Column ids in the builder are "<builderNetworkKey>.<metricKey>"; the API
 * wants bare metric keys per network, and its own network keys ("ga", not
 * "google_analytics").
 */
/** Builder network key → API network key, where the two differ. */
const API_NETWORK_KEY = { google_analytics: "ga" };
export const toApiNetwork = (key) => API_NETWORK_KEY[key] || key;
/** Builder column id "google_analytics.sessions" → report column key "ga.sessions". */
export const toApiColumnKey = (id) => {
    const dot = id.indexOf(".");
    return `${toApiNetwork(id.slice(0, dot))}${id.slice(dot)}`;
};
/** API network key → builder key ("ga" → "google_analytics"). */
const toBuilderNetwork = (apiKey) => (Object.keys(API_NETWORK_KEY).find((key) => API_NETWORK_KEY[key] === apiKey) ||
    apiKey);
/** Report column key "ga.sessions" → builder column id "google_analytics.sessions". */
const toBuilderColumnId = (key) => {
    const dot = key.indexOf(".");
    return dot < 0 ? key : `${toBuilderNetwork(key.slice(0, dot))}${key.slice(dot)}`;
};
/**
 * A saved report (`saved_report` from network-report with a report_id) →
 * builder selection — the reverse of `buildNetworkReportPayload`, so Edit opens
 * on exactly what was saved.
 */
export const payloadToBuilderState = (saved) => {
    const payload = saved.report_payload || {};
    const entries = Array.isArray(payload.networks) ? payload.networks : [];
    const networkKeys = entries.map((entry) => toBuilderNetwork(String(entry.network)));
    // The saved drag order when there is one, else each network's columns in turn.
    const fromNetworks = entries.flatMap((entry) => (entry.selected_columns || []).map((metric) => `${toBuilderNetwork(entry.network)}.${metric}`));
    const columnOrder = Array.isArray(payload.column_order) && payload.column_order.length
        ? payload.column_order.map(toBuilderColumnId)
        : fromNetworks;
    const byNetwork = (pick) => Object.fromEntries(entries
        .map((entry) => [toBuilderNetwork(entry.network), pick(entry)])
        .filter(([, value]) => value !== undefined));
    return {
        report_name: saved.report_name || "",
        template_id: "tpl_blank",
        network_keys: networkKeys,
        column_order: columnOrder,
        dimension_ids: Array.isArray(payload.dimensions) ? [...payload.dimensions] : [],
        // The API stores one value per filter; the builder shows one row each.
        filters_by_network: byNetwork((entry) => (entry.filters || []).map((filter, index) => ({
            id: `${entry.network}-${index}`,
            field_id: filter.field,
            operator: filter.operator,
            values: [String(filter.value ?? "")],
        }))),
        breakdowns_by_network: byNetwork((entry) => entry.breakdowns),
        enum_filters_by_network: byNetwork((entry) => entry.enum_filters),
        source_by_network: byNetwork((entry) => entry.source),
        custom_metrics: (saved.custom_metrics_payload || []).map((metric) => ({
            key: metric.key,
            name: metric.name,
            description: metric.description || "",
            format: metric.format,
            position: metric.position,
            formula: metric.formula,
        })),
        period_start: payload.primary_start_date,
        period_end: payload.primary_end_date,
        compare_start: payload.compare ? payload.compare_start_date ?? null : null,
        compare_end: payload.compare ? payload.compare_end_date ?? null : null,
    };
};
export const buildNetworkReportPayload = (state, clientId, networkInfo = []) => {
    const compare = Boolean(state.compare_start && state.compare_end);
    const hasCampaign = state.dimension_ids.includes("campaign");
    const networks = state.network_keys
        .map((key) => {
        const prefix = `${key}.`;
        const selected_columns = state.column_order
            .filter((id) => id.startsWith(prefix))
            .map((id) => id.slice(prefix.length));
        // One API filter per value — the API ANDs its filters and takes a
        // single value each.
        const filters = (state.filters_by_network[key] || []).flatMap((filter) => {
            const field = filter.field_id;
            return filter.values
                .map((value) => value.trim())
                .filter(Boolean)
                .map((value) => ({ field, operator: filter.operator, value }));
        });
        const info = networkInfo.find((n) => n.key === key);
        // Campaign Name is always part of a Campaign breakdown where the
        // network offers it (Meta): the Campaign column IS that name, so the
        // builder shows it ticked and locked rather than as a choice.
        const pickedBreakdowns = hasCampaign ? state.breakdowns_by_network?.[key] || [] : [];
        const breakdowns = hasCampaign &&
            info?.breakdownFields?.some((field) => field.key === "campaign_name") &&
            !pickedBreakdowns.includes("campaign_name")
            ? ["campaign_name", ...pickedBreakdowns]
            : pickedBreakdowns;
        // Email's source; what that source cannot carry is left out
        // (Klaviyo Flow has no Email / SMS channel).
        const sourceKey = info?.sourceChoices.length
            ? state.source_by_network?.[key] || info.defaultSource || info.sourceChoices[0].key
            : undefined;
        const source = info?.sourceChoices.find((choice) => choice.key === sourceKey);
        const picked = { ...state.enum_filters_by_network?.[key] };
        (info?.enumFields || []).forEach((field) => {
            if (field.singleSelect && !picked[field.key]?.length && field.values[0]) {
                picked[field.key] = [field.values[0]];
            }
        });
        const enumFilters = Object.fromEntries(Object.entries(picked).filter(([field, values]) => values.length > 0 && !source?.unsupportedEnumFields.includes(field)));
        return {
            network: toApiNetwork(key),
            ...(sourceKey ? { source: sourceKey } : {}),
            selected_columns,
            filters: source
                ? filters.filter((f) => !source.unsupportedFilterFields.includes(f.field))
                : filters,
            ...(Object.keys(enumFilters).length ? { enum_filters: enumFilters } : {}),
            ...(breakdowns.length ? { breakdowns } : {}),
        };
    })
        // A network with no ticked column adds nothing to the report.
        .filter((entry) => entry.selected_columns.length > 0);
    return {
        client_id: clientId,
        primary_start_date: state.period_start,
        primary_end_date: state.period_end,
        compare,
        ...(compare
            ? { compare_start_date: state.compare_start, compare_end_date: state.compare_end }
            : {}),
        row_limit: 5000,
        networks,
        dimensions: [...state.dimension_ids],
        column_order: state.column_order.map(toApiColumnKey),
        ...(state.custom_metrics?.length
            ? {
                custom_metrics_payload: state.custom_metrics.map(({ key, name, description, format, position, formula }) => ({
                    key,
                    name,
                    format,
                    position,
                    formula,
                    ...(description?.trim() ? { description: description.trim() } : {}),
                })),
            }
            : {}),
    };
};
